import type { SupabaseClient } from "@supabase/supabase-js";
import { createEdgeProvider } from "./edgeProvider";
import { resolveDataSource, type PortfolioDataProvider } from "./provider";

/**
 * The `local` and `mock` providers pull in the whole business-logic core, so they are loaded
 * through a `import.meta.env.DEV` guarded dynamic import. Vite eliminates that branch in a
 * production build, keeping the calculations out of the shipped bundle.
 */
export async function createPortfolioDataProvider(supabase: SupabaseClient): Promise<PortfolioDataProvider> {
	const source = resolveDataSource();

	if (source === "edge") {
		return createEdgeProvider(supabase);
	}

	if (!import.meta.env.DEV) {
		console.warn(`Data source "${source}" is unavailable in a production build; falling back to the edge function.`);
		return createEdgeProvider(supabase);
	}

	if (source === "mock") {
		const { createMockProvider } = await import("./mockProvider");
		return createMockProvider();
	}

	const { createLocalProvider } = await import("./localProvider");
	return createLocalProvider(supabase);
}

export type { PortfolioDataProvider, DataSource } from "./provider";
